import React, { useState } from 'react';
import './DocumentComparison.css';

const DocumentComparison = ({ documents, onClose }) => {
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);

  const selectDocument = (doc) => {
    if (selectedDocs.find(d => d.id === doc.id)) {
      setSelectedDocs(selectedDocs.filter(d => d.id !== doc.id));
    } else if (selectedDocs.length < 2) {
      setSelectedDocs([...selectedDocs, doc]);
    }
  };

  const compareDocuments = () => {
    if (selectedDocs.length !== 2) return;

    const [doc1, doc2] = selectedDocs;
    
    // Simulate document comparison
    const similarities = {
      fileSize: Math.abs(doc1.fileSize - doc2.fileSize) < 100000 ? 95 : 60,
      uploadTime: Math.abs(new Date(doc1.uploadedAt) - new Date(doc2.uploadedAt)) < 3600000 ? 90 : 70,
      verificationScore: Math.abs(doc1.verificationScore - doc2.verificationScore) < 10 ? 85 : 50,
      documentType: doc1.documentType === doc2.documentType ? 100 : 0
    };

    const overallSimilarity = Object.values(similarities).reduce((a, b) => a + b, 0) / 4;

    setComparisonResult({
      doc1,
      doc2,
      similarities,
      overallSimilarity: Math.round(overallSimilarity),
      recommendation: overallSimilarity > 80 ? 'Documents appear to be similar or duplicates' : 
                    overallSimilarity > 60 ? 'Documents have some similarities' : 
                    'Documents appear to be different'
    });
  };

  const resetComparison = () => {
    setSelectedDocs([]);
    setComparisonResult(null);
  };

  return (
    <div className="comparison-modal">
      <div className="comparison-content">
        <div className="comparison-header">
          <h3>📊 Document Comparison Tool</h3>
          <button className="close-comparison" onClick={onClose} aria-label="Close document comparison">✕</button>
        </div>

        {!comparisonResult ? (
          <div className="comparison-selection">
            <p>Select 2 documents to compare:</p>
            <div className="documents-grid">
              {documents.map(doc => (
                <div 
                  key={doc.id}
                  className={`comparison-doc-card ${selectedDocs.find(d => d.id === doc.id) ? 'selected' : ''}`}
                  onClick={() => selectDocument(doc)}
                >
                  <div className="doc-info">
                    <h5>{doc.documentType.replace('_', ' ')}</h5>
                    <small>{doc.fileName}</small>
                    <small>Score: {doc.verificationScore}%</small>
                  </div>
                  {selectedDocs.find(d => d.id === doc.id) && (
                    <div className="selection-indicator">✓</div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="comparison-actions">
              <button 
                className="btn btn-primary"
                onClick={compareDocuments}
                disabled={selectedDocs.length !== 2}
              >
                Compare Documents
              </button>
              <button className="btn btn-secondary" onClick={resetComparison}>
                Reset Selection
              </button>
            </div>
          </div>
        ) : (
          <div className="comparison-results">
            <div className="compared-docs">
              <div className="doc-preview">
                <h4>Document 1</h4>
                <p><strong>Type:</strong> {comparisonResult.doc1.documentType}</p>
                <p><strong>File:</strong> {comparisonResult.doc1.fileName}</p>
                <p><strong>Score:</strong> {comparisonResult.doc1.verificationScore}%</p>
              </div>
              
              <div className="comparison-vs">VS</div>
              
              <div className="doc-preview">
                <h4>Document 2</h4>
                <p><strong>Type:</strong> {comparisonResult.doc2.documentType}</p>
                <p><strong>File:</strong> {comparisonResult.doc2.fileName}</p>
                <p><strong>Score:</strong> {comparisonResult.doc2.verificationScore}%</p>
              </div>
            </div>

            <div className="similarity-metrics">
              <h4>Similarity Analysis</h4>
              <div className="metrics-grid">
                <div className="metric">
                  <span>File Size Similarity</span>
                  <div className="metric-bar">
                    <div 
                      className="metric-fill" 
                      style={{ width: `${comparisonResult.similarities.fileSize}%` }}
                    ></div>
                  </div>
                  <span>{comparisonResult.similarities.fileSize}%</span>
                </div>
                
                <div className="metric">
                  <span>Upload Time Similarity</span>
                  <div className="metric-bar">
                    <div 
                      className="metric-fill" 
                      style={{ width: `${comparisonResult.similarities.uploadTime}%` }}
                    ></div>
                  </div>
                  <span>{comparisonResult.similarities.uploadTime}%</span>
                </div>
                
                <div className="metric">
                  <span>Verification Score Similarity</span>
                  <div className="metric-bar">
                    <div 
                      className="metric-fill" 
                      style={{ width: `${comparisonResult.similarities.verificationScore}%` }}
                    ></div>
                  </div>
                  <span>{comparisonResult.similarities.verificationScore}%</span>
                </div>
                
                <div className="metric">
                  <span>Document Type Match</span>
                  <div className="metric-bar">
                    <div 
                      className="metric-fill" 
                      style={{ width: `${comparisonResult.similarities.documentType}%` }}
                    ></div>
                  </div>
                  <span>{comparisonResult.similarities.documentType}%</span>
                </div>
              </div>

              <div className="overall-similarity">
                <h4>Overall Similarity: {comparisonResult.overallSimilarity}%</h4>
                <p className="recommendation">{comparisonResult.recommendation}</p>
              </div>
            </div>

            <div className="comparison-actions">
              <button className="btn btn-primary" onClick={resetComparison}>
                Compare Other Documents
              </button>
              <button className="btn btn-secondary" onClick={onClose}>
                Close Comparison
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentComparison;